import EventBus from 'App/services/EventBus';
import type { IntegrationConfig } from 'App/resources/AllIWant';
import type React from 'react';

/*
	Standalone replacement for Tools.VoiceService (WEB-7834).

	A 1:1 port of the Angular services.voice/VoiceService service
	(upsales/common/directives/react-roots/upVoiceRoot.js), which now delegates to this module.

	The delegation is not cosmetic: the module-level state below (ongoingCall / callState /
	callStart / calleeInfo) is written by the Angular tree (upVoiceRoot's controller calls
	callStarted, callEnded and setCallInfo as the softphone progresses) and read by React
	(EditPhonecallCallBlock, EditPhonecallActivityOutcome, upFlashRoot, ui/Modal). A second
	instance would report "no call in progress" forever, with no error and no failing test.

	Every $rootScope.$broadcast is now EventBus.broadcast. The Angular original was inconsistent
	about which scope it used — hangUp reached for the Tools.$rootScope global while every sibling
	used the injected $rootScope — but in Angular those are the same object, so routing both through
	EventBus (a facade over that very $rootScope) unifies them with no behaviour change. Listeners
	registered with Tools.$rootScope.$on still receive all four channels.
*/

type Named = { name?: string; id?: number };

export type CallInfoPayload = {
	contact?: Named | null;
	client?: Named | null;
	/*
		Declared `number` to match EditPhonecallCallBlock's pre-existing CallData/State types, but at
		runtime this is a STRING — upVoice.js builds it as `prop[...].replace(/ /g, '')`. The mismatch
		predates this module and is only ever rendered as text, so it is carried over rather than fixed
		here; correcting it means retyping the component, which belongs in its own change.
	*/
	number?: number | null;
	related?: unknown;
};

export type CallInfo = {
	callState: string;
	callStart: number | null;
	calleeInfo: CallInfoPayload | null;
};

let ongoingCall = false;
// idle, dialing or inCall
let callState = 'idle';
let callStart: number | null = null;
let calleeInfo: CallInfoPayload | null = null;

export const callStarted = () => {
	EventBus.broadcast('callStatusChanged', { ongoingCall: true });
	ongoingCall = true;
};

// Deliberately nulls callStart but NOT calleeInfo — only setCallInfo('idle') clears the callee.
export const callEnded = () => {
	EventBus.broadcast('callStatusChanged', { ongoingCall: false });
	ongoingCall = false;
	callStart = null;
};

export const callInProgress = () => ongoingCall;

export const getCallInfo = (): CallInfo => ({ callState, callStart, calleeInfo });

export const setCallInfo = (
	state?: string | null,
	callStartTime?: number | null,
	onGoingCallInfo?: CallInfoPayload | null
) => {
	callState = state || callState;
	if (callState === 'idle') {
		callStart = null;
		calleeInfo = null;
	} else {
		callStart = callStartTime || callStart;
		calleeInfo = onGoingCallInfo || calleeInfo;
	}
	EventBus.broadcast('callInfoChange', { callState, callStart, calleeInfo });
};

/*
	A no-op while a call is already in progress, as in the Angular original.

	The trailing setCallInfo(null, null, onGoingCallInfo) does NOT record the callee when the state
	is still 'idle' — setCallInfo nulls calleeInfo on the idle branch before it ever looks at the
	argument. That is pre-existing and load-bearing in practice: upVoiceRoot's 'openVoice' listener
	moves the state to 'dialing' and sets the callee itself. Kept verbatim.
*/
export const callClient = (client: Named | null, number: number | null, related?: unknown) => {
	if (!ongoingCall) {
		const onGoingCallInfo = { client, number, related };
		EventBus.broadcast('openVoice', onGoingCallInfo);
		setCallInfo(null, null, onGoingCallInfo);
	}
};

// Same no-op guard; the client is derived off the contact rather than passed in.
export const callContact = (
	contact: (Named & { client?: Named | null }) | null,
	number: number | null,
	related?: unknown
) => {
	if (!ongoingCall) {
		const onGoingCallInfo = { contact, client: contact?.client, number, related };
		EventBus.broadcast('openVoice', onGoingCallInfo);
		setCallInfo(null, null, onGoingCallInfo);
	}
};

export const hangUp = () => {
	EventBus.broadcast('hangUpVoiceReq');
};

export type DialProps = {
	href: string;
	target: string;
	onClick: (event: React.MouseEvent<HTMLElement>) => void;
};

/* What the dial rule needs off the state. Passed in rather than read from a global so this
   stays a pure getter - see the file header of Store/selectors/AppSelectors. */
export type DialState = {
	phoneIntegration: IntegrationConfig | null;
	params: { callToType: string; callToTarget: string; telPrefix: string };
};

export type DialParty = {
	type: 'client' | 'contact';
	record: (Named & { client?: Named | null }) | null;
};

/*
	Everything that is not a digit is dropped so a number stored as '08-123 45 67' still dials, a
	leading '+' survives, and a leading national '0' becomes the account's telPrefix. An app that
	takes the number from the OS declares its own scheme (capabilities.useCallToLinks); everything
	else falls back to the account's callToType param.
*/
function dialHref(number: string, { phoneIntegration, params }: DialState): { href: string; target: string } {
	const scheme = phoneIntegration?.capabilities?.useCallToLinks || params.callToType;

	let dialled = '';
	for (let index = 0; index < number.length; index += 1) {
		const char = number[index];
		if (index === 0 && char === '0' && params.telPrefix) {
			dialled += params.telPrefix;
		} else if (index === 0 && char === '+') {
			dialled += char;
		} else if (/[0-9]/.test(char)) {
			dialled += char;
		}
	}

	return { href: `${scheme}${dialled}`, target: params.callToTarget };
}

/*
	The anchor props a clickable phone number needs, so the number reaches the softphone rather
	than the OS. Null for an empty number, so a call site renders nothing.

	`party` is who the call is logged against, and its record is allowed to be null - a number
	belonging to no CRM record (a prospecting hit, Upsales' own billing line) still dials, and the
	softphone shows an unknown party rather than refusing it.

	Where a telephony app is connected the click is swallowed and the app dials, EXCEPT for an app
	that takes the number from the OS - there the href IS the dialling mechanism and has to be
	allowed to open, while the app is still told which records the call is about.
*/
export const getDialPropsFromState = (
	number: string | null | undefined,
	party: DialParty,
	state: DialState,
	related?: unknown
): DialProps | null => {
	if (!number) {
		return null;
	}

	const stripped = number.replace(/ /g, '');

	return {
		...dialHref(stripped, state),
		onClick: event => {
			event.stopPropagation();
			if (!state.phoneIntegration) {
				return;
			}
			if (!state.phoneIntegration.capabilities?.useCallToLinks) {
				event.preventDefault();
			}
			/* Declared `number` on CallInfoPayload but a string at runtime - see the comment on
			   that type. */
			const dialledNumber = stripped as unknown as number;
			if (party.type === 'contact') {
				callContact(party.record, dialledNumber, related);
			} else {
				callClient(party.record, dialledNumber, related);
			}
		}
	};
};
